import { toaster } from 'components'
import { isEqual } from 'lodash-es'
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { CloudAsset, MediaWithUrl, TemplateDocs } from 'types'
import { mergeObjects, useOnline } from 'utils'
import { allMediaFilesExist, getCloudAssets, getDocuments } from '../../utils/appData'
import {
  ensureDirectoryExists,
  listenToFileSave,
  makeFileSrc,
  saveFile,
} from '../../utils/filesystem'
import { isDesktop } from '../../utils/helpers'
import { templateOverride } from '../../utils/templateOverride'
import { useExplorerConfig } from './ExplorerConfigProvider'

export interface ILoadingContext {
  copyLatestTemplate?: () => void
  status: LoadingStatus
  error: string
  assetProgress: number
}

export type LoadingStatus =
  | 'initializing'
  | 'getting-documents'
  | 'getting-asset-meta'
  | 'getting-assets'
  | 'done'

export const LoadingContext = createContext<ILoadingContext>({} as any)

interface FileProgress extends CloudAsset {
  transferredBytes: number
}

export function LoadingProvider({ children }: { children: ReactNode }) {
  const online = useOnline()
  const { facility, updateTemplateDocs, templateDocs, baseTemplate, theme, workspace } =
    useExplorerConfig()

  const [status, setStatus] = useState<LoadingStatus>(templateOverride ? 'done' : 'initializing')

  const [cloudAssets, setCloudAssets] = useState<CloudAsset[]>()
  const [updatingFiles, setUpdatingFiles] = useState<FileProgress[]>([])
  const [error, setError] = useState('')

  const assetsToUpdate = useMemo(
    () => cloudAssets?.filter(a => a.status !== 'unchanged'),
    [cloudAssets]
  )
  const { totalBytes, transferredBytes } = getProgress(updatingFiles)

  useEffect(() => {
    setStatus(templateOverride ? 'done' : 'initializing')
  }, [workspace])

  useEffect(() => {
    fetchDocuments()
  }, [baseTemplate, online])

  useEffect(() => {
    if (!assetsToUpdate || !online) return

    fetchAssets()
  }, [assetsToUpdate, online])

  async function fetchDocuments() {
    setCloudAssets(undefined)
    setUpdatingFiles([])

    if (!online) {
      if (!templateDocs) setError('Connect to the internet')
      else {
        allMediaFilesExist(templateDocs.media).then(allExist => {
          if (allExist) setStatus('done')
          else setError('Connect to the internet')
        })
      }

      return
    }

    if (!baseTemplate) return

    const template = mergeObjects(baseTemplate, facility?.template)
    const noChange = templateDocs && isEqual(templateDocs.template, template)
    if (noChange) return fetchAssetMeta(templateDocs)

    setStatus('getting-documents')
    try {
      const docs = await getDocuments(template)
      updateTemplateDocs(docs)
      fetchAssetMeta(docs)
    } catch (e: any) {
      console.error(e)
      switch (e.code) {
        case 'permission-denied':
          return setError('You do not have permission')
        default:
          return setError('Error downloading files')
      }
    }
  }

  function fetchAssetMeta(docs: TemplateDocs) {
    if (!isDesktop) return setStatus('done')

    setStatus('getting-asset-meta')
    getCloudAssets(docs.media).then(setCloudAssets)
  }

  async function fetchAssets() {
    if (!assetsToUpdate?.length) return done()

    setStatus('getting-assets')
    updateFiles(assetsToUpdate, setUpdatingFiles)
      .then(done)
      .catch(e => {
        console.error(e)
        setError('Error downloading files')
      })
  }

  const copyLatestTemplate = baseTemplate
    ? async () => {
        await navigator.clipboard.writeText(JSON.stringify(baseTemplate))
        toaster.create({ title: 'Template copied', type: 'success' })
      }
    : undefined

  async function done() {
    const updatedMedia: MediaWithUrl[] = await Promise.all(
      templateDocs!.media.map(async m => {
        const asset = cloudAssets?.find(f => f.id === m.id)
        if (!asset) return m

        const url = await makeFileSrc(asset.filePath)
        const coverUrl = asset.coverFilePath && (await makeFileSrc(asset.coverFilePath))
        return { ...m, url, coverUrl }
      })
    )
    if (!isEqual(templateDocs!.media, updatedMedia))
      updateTemplateDocs(s => ({ ...s!, media: updatedMedia }))

    setStatus('done')
  }

  return (
    <LoadingContext.Provider
      value={{
        copyLatestTemplate,
        status,
        error,
        assetProgress:
          status === 'getting-assets' && totalBytes ? transferredBytes / totalBytes : -1,
      }}
    >
      {children}
    </LoadingContext.Provider>
  )
}

function getProgress(files: FileProgress[]) {
  let totalBytes = 0
  let transferredBytes = 0
  for (let file of files) {
    totalBytes += file.totalBytes
    transferredBytes += file.transferredBytes
  }

  return { totalBytes, transferredBytes }
}

async function updateFiles(assets: CloudAsset[], onUpdate: (files: FileProgress[]) => void) {
  let transferred: FileProgress[] = assets.map(f => ({ ...f, transferredBytes: 0 }))
  let updateInterval = setInterval(() => onUpdate(transferred), 50)
  const unlisten = await listenToFileSave(data => {
    transferred = transferred.map(file => {
      if (file.filePath === data.path) {
        file.transferredBytes = data.transferred_bytes
        file.totalBytes = data.total_bytes
      }
      return file
    })
  })

  try {
    await createMediaDirectories()
    await Promise.all(
      assets.map(async ({ downloadUrl, filePath, coverDownloadUrl, coverFilePath }) => {
        await saveFile(downloadUrl, filePath)
        const hasCover = coverDownloadUrl && coverFilePath
        if (hasCover) await saveFile(coverDownloadUrl, coverFilePath)
      })
    )
  } finally {
    unlisten()
    clearInterval(updateInterval)
  }
}

async function createMediaDirectories() {
  await ensureDirectoryExists('media/images')
  await ensureDirectoryExists('media/videos')
  await ensureDirectoryExists('media/pdfs')
}
